import type { useFetcher } from '@remix-run/react'
import type { SerializeFrom } from '@remix-run/server-runtime'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Media } from '~/components/Media/Media'
import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import type { CartFull } from '~/models/cart.server'
import type { CartLineFull } from '~/models/cart_line.server'
import { Counter } from '~/ui'
import { cn } from '~/utils/classnames'
import { getActiveShopPromotions } from '~/utils/promotion'

import { Price } from '../price'

interface CartItemCardProps {
  fetcher: ReturnType<typeof useFetcher>
  cart: SerializeFrom<CartFull>
  item: SerializeFrom<CartLineFull>
  className?: string
}

export const CartItemCard = ({
  fetcher,
  cart,
  item,
  className,
}: CartItemCardProps) => {
  const { t } = useTranslation()
  const shop = useShop()

  const [quantity, setQuantity] = useState(item.quantity)

  const media = useMemo(() => {
    return item.productVariant.attachments?.[0]?.media
  }, [item.productVariant.attachments])

  const traits = useMemo(() => {
    return item.productVariant.markers.map(({ trait }) => trait.name)
  }, [item])

  const price = useMemo(() => {
    const promotions = getActiveShopPromotions(cart, shop)
    const promotion = promotions.find(({ type }) => type === 'xPercentOff')

    if (promotion) {
      const discount = (item.productVariant.price * promotion.amount) / 100

      return {
        current: item.productVariant.price - discount,
        previous: item.productVariant.price,
      }
    }

    return {
      current: item.productVariant.price,
      previous: null,
    }
  }, [cart, item.productVariant.price, shop])

  const hasStock = useMemo(() => {
    return (
      item.productVariant.stock > 0 ||
      !item.productVariant.product.isStrictStock
    )
  }, [item.productVariant.stock, item.productVariant.product.isStrictStock])

  const handleQuantityChange = (newQuantity: number) => {
    setQuantity(newQuantity)

    fetcher.submit(
      { productVariantId: item.productVariant.id, quantity: newQuantity },
      { method: 'POST', action: makeShopOwnPath(shop, '/cart') }
    )
  }

  if (quantity === 0) return null

  return (
    <div
      className={cn(
        'text-foreground bg-over dark:bg-under/8 overall:bg-under/8 flex gap-3 rounded-md p-3 text-sm',
        className
      )}
    >
      {media && (
        <figure className="bg-foreground/8 aspect-square w-22 overflow-hidden rounded-sm">
          <Media
            kind={media.kind}
            src={media.src}
            className="size-full object-cover"
            transformation={{
              width: 200,
              format: 'webp',
              resizeMode: 'fill',
            }}
          />
        </figure>
      )}
      <div className="h-ful flex flex-col">
        <p className="font-medium">{item.productVariant.product.name}</p>
        <p className="text-foreground/64">{traits.join(' · ')}</p>
        <Counter className="mt-auto">
          <Counter.Input
            min={0}
            max={
              item.productVariant.product.isStrictStock
                ? item.productVariant.stock
                : undefined
            }
            value={quantity}
            onChange={(event) => {
              handleQuantityChange(Number(event.target.value))
            }}
          />
        </Counter>
      </div>
      <div className="ml-auto flex flex-col items-end font-medium">
        {hasStock ? (
          <>
            <Price amount={price.current} />
            {!!price.previous && (
              <Price variant="strikethrough" amount={price.previous} />
            )}
          </>
        ) : (
          <div className="bg-accent-primary text-2xs/none text-accent-foreground h-fit rounded-md px-1 py-0.75 font-medium">
            {t('components.inventory.out_of_stock')}
          </div>
        )}
      </div>
    </div>
  )
}
